import { useState } from "react";
import { Sparkles, Check } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";

const SERVICE_OPTIONS = [
  "Branding & Identity", "Logo Design", "Social Media Management", "Google Ads",
  "Meta Ads", "SEO", "Lead Generation", "Reels Production", "Ad Film",
  "Photography", "Website", "E-Commerce", "Studio Rental", "Influencer Marketing",
];

export default function Quote() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", email: "", business_type: "", services: [],
    budget: "", timeline: "", requirements: "",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = (s) => setForm((f) => ({
    ...f,
    services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
  }));

  const submit = async () => {
    if (!form.name || !form.email || form.services.length === 0) {
      toast.error("Add your name, email, and pick at least one service"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/ai/quote", form);
      setResult(data.quote);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "AI quote failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="quote-page" className="pt-24">
      <section className="container mx-auto px-4 pt-16 pb-24 max-w-3xl">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF007F] mb-3">AI Quote Generator</div>
        <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-none mb-4">
          Your tailored proposal in <span className="text-gradient">30 seconds.</span>
        </h1>
        <p className="text-lg text-[#52525B] mb-10">
          Tell us about your business — our AI strategist drafts an indicative scope, timeline and budget.
        </p>

        {result ? (
          <div className="bg-white border-2 border-[#FF007F] rounded-2xl p-8" data-testid="quote-result">
            <div className="flex items-center gap-2 mb-4">
              <Check className="w-5 h-5 text-[#00BFE0]" />
              <div className="text-xs font-mono uppercase tracking-widest text-[#00BFE0]">Your AI Proposal</div>
            </div>
            <div className="prose prose-zinc max-w-none whitespace-pre-wrap font-sans leading-relaxed">{result}</div>
            <div className="mt-8 pt-6 border-t border-zinc-200">
              <a href={`mailto:info@cftcreations.com?subject=Quote%20Follow-up%20-%20${encodeURIComponent(form.name)}`}
                className="inline-flex px-6 py-3 rounded-full bg-gradient-brand text-white font-bold">
                Book a Consultation →
              </a>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-zinc-200 rounded-2xl p-8 space-y-6">
            <Stepper step={step} />

            {step === 1 && (
              <>
                <h3 className="font-display font-bold text-xl">Tell us about you</h3>
                <input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="quote-name" className="w-full px-4 py-3 border border-zinc-300 rounded-lg" />
                <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="quote-email" className="w-full px-4 py-3 border border-zinc-300 rounded-lg" />
                <input placeholder="Business type (e.g. Restaurant, SaaS, Fashion)" value={form.business_type}
                  onChange={(e) => setForm({ ...form, business_type: e.target.value })}
                  data-testid="quote-business" className="w-full px-4 py-3 border border-zinc-300 rounded-lg" />
              </>
            )}

            {step === 2 && (
              <>
                <h3 className="font-display font-bold text-xl">Pick your services</h3>
                <div className="grid sm:grid-cols-2 gap-2">
                  {SERVICE_OPTIONS.map((s) => (
                    <button key={s} type="button" onClick={() => toggle(s)} data-testid={`quote-svc-${s}`}
                      className={`text-left px-4 py-3 rounded-lg border text-sm font-medium transition ${
                        form.services.includes(s)
                          ? "bg-[#0A1128] text-white border-[#0A1128]"
                          : "border-zinc-300 hover:border-[#FF007F]"
                      }`}>
                      {s}
                    </button>
                  ))}
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <h3 className="font-display font-bold text-xl">Budget & timeline</h3>
                <select value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })}
                  className="w-full px-4 py-3 border border-zinc-300 rounded-lg">
                  <option value="">Select budget range (optional)</option>
                  <option>Under ₹50,000</option><option>₹50,000 – ₹2L</option>
                  <option>₹2L – ₹5L</option><option>₹5L – ₹15L</option><option>₹15L+</option>
                </select>
                <select value={form.timeline} onChange={(e) => setForm({ ...form, timeline: e.target.value })}
                  className="w-full px-4 py-3 border border-zinc-300 rounded-lg">
                  <option value="">When do you want to start? (optional)</option>
                  <option>Immediately</option><option>This month</option>
                  <option>Next month</option><option>Within 90 days</option>
                </select>
                <textarea rows={4} value={form.requirements} placeholder="Anything specific we should know?"
                  onChange={(e) => setForm({ ...form, requirements: e.target.value })}
                  className="w-full px-4 py-3 border border-zinc-300 rounded-lg" />
              </>
            )}

            <div className="flex justify-between gap-3 pt-2">
              {step > 1 ? (
                <button onClick={() => setStep(step - 1)} className="px-5 py-2.5 rounded-full border border-zinc-300 text-sm font-semibold">Back</button>
              ) : <span />}
              {step < 3 ? (
                <button onClick={() => setStep(step + 1)} data-testid="quote-next"
                  className="px-6 py-2.5 rounded-full bg-[#0A1128] text-white text-sm font-bold">Next</button>
              ) : (
                <button onClick={submit} disabled={busy} data-testid="quote-submit"
                  className="px-6 py-2.5 rounded-full bg-gradient-brand text-white text-sm font-bold disabled:opacity-50 inline-flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> {busy ? "Generating…" : "Generate Proposal"}
                </button>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function Stepper({ step }) {
  return (
    <div className="flex gap-2 mb-2">
      {[1, 2, 3].map((s) => (
        <div key={s} className={`flex-1 h-1.5 rounded-full ${s <= step ? "bg-gradient-brand" : "bg-zinc-200"}`} />
      ))}
    </div>
  );
}
